/**
 * A dashboard card's title.
 *
 * A card can carry a name the user typed (WPS-232 #3: "ability to name
 * cards"). A card without one is named from its data. That name comes from the
 * server (the catalogue's ``titles``, built by ``apps/dashboards/titles.py`` in
 * the tenant's own vocabulary), so the platform has one naming rule, not a copy
 * here that could drift or miss a tenant's words.
 */

import type { DashboardCatalog } from '@/admin/hooks/use-dashboard-config'
import type { TileDef } from './dashboard-types'

/** Longest name a card can be given. */
export const CARD_TITLE_MAX = 60

/** The entity + measure a built card is bound to, or null for a bespoke card. */
export function cardBinding(tile: TileDef): { entity: string; measure: string } | null {
  // A Raw data table is a live list of one measure's records.
  // Its card's source is `entity` when the records come from another list
  // (Warranties' Pending exceptions lists registrations).
  if (tile.data_set?.source)
    return { entity: tile.data_set.entity ?? tile.data_set.source, measure: tile.data_set.measure ?? 'list' }
  const key = tile.data_set?.key
  if (key && key.includes(':')) {
    const [entity, measure] = key.split(':')
    return { entity, measure }
  }
  return null
}

/**
 * The data-derived name for an entity x measure. Falls back to the catalogue
 * labels ("Claims by product") only while the server's titles are loading.
 */
export function derivedTitleFor(
  entityKey: string,
  measureKey: string,
  catalog: DashboardCatalog | undefined,
): string {
  if (!catalog) return ''
  const fromServer = catalog.titles?.[`${entityKey}:${measureKey}`]
  if (fromServer) return fromServer
  const entity = catalog.entities?.find((e) => e.key === entityKey)
  const measure = catalog.measures?.find((m) => m.key === measureKey)
  if (!entity || !measure) return ''
  return `${entity.label} ${measure.label.toLowerCase()}`
}

/** The title a card shows: the name the user gave it, else one from its data. */
export function cardTitle(tile: TileDef, catalog: DashboardCatalog | undefined): string {
  const typed = tile.title?.trim()
  if (typed) return typed
  const binding = cardBinding(tile)
  return binding ? derivedTitleFor(binding.entity, binding.measure, catalog) : ''
}
